/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { usePublishEvent } from '@agilelab/plugin-wb-events';
import { creationTemplateNavigatedTopic } from '@agilelab/plugin-wb-events-common';
import {
  customAlertApiRef,
  MainPageType,
  useDynamicBreadcrumbLink,
  WbBreadcrumb,
  WbBreadcrumbs,
  WbCard,
  WbHeader,
} from '@agilelab/plugin-wb-platform';
import { CustomError } from '@agilelab/plugin-wb-platform-common';
import { stringifyEntityRef } from '@mainstage/catalog-model';
import { Content } from '@mainstage/core-components';
import {
  AnalyticsContext,
  useApi,
  useRouteRef,
  useRouteRefParams,
} from '@mainstage/core-plugin-api';
import { LinearProgress } from '@material-ui/core';
import { IChangeEvent } from '@rjsf/core';
import qs from 'qs';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { scaffolderApiRef } from '../../api';
import { FieldExtensionOptions } from '../../extensions/types';
import { LayoutOptions } from '../../layouts';
import {
  rootRouteRef,
  scaffolderTaskRouteRef,
  selectedTemplateRouteRef,
} from '../../routes';
import { MultiStepJsonForm, reviewStep } from '../MultiStepJsonForm';
import { SecretsContext } from '../secrets/SecretsContext';
import { useTemplateParameterSchema } from '../hooks';
import { WbPage } from '@agilelab/plugin-wb-platform';
import { useFormSteps } from '../MultiStepJsonForm/useFormSteps';

export const DEFAULT_FIELD_EXTENSIONS = [];

export const TemplatePage = ({
  customFieldExtensions = DEFAULT_FIELD_EXTENSIONS,
  layouts,
}: {
  customFieldExtensions?: FieldExtensionOptions<any, any>[];
  layouts?: LayoutOptions[];
}) => {
  const secretsContext = useContext(SecretsContext);
  const alertApi = useApi(customAlertApiRef);

  const scaffolderApi = useApi(scaffolderApiRef);
  const { templateName, namespace } = useRouteRefParams(
    selectedTemplateRouteRef,
  );
  const templateRef = stringifyEntityRef({
    name: templateName,
    kind: 'template',
    namespace,
  });
  const { publish } = usePublishEvent({
    generatorSystem: 'builder',
    keyBuilderCatalog: templateRef,
  });

  const navigate = useNavigate();
  const scaffolderTaskRoute = useRouteRef(scaffolderTaskRouteRef);
  const rootRoute = useRouteRef(rootRouteRef);
  const breadcrumbsLinkWithQueryString = useDynamicBreadcrumbLink(
    rootRoute,
    MainPageType.BUILDER,
  );

  const {
    schema,
    loading: schemaLoading,
    error: schemaError,
  } = useTemplateParameterSchema(templateRef);

  const {
    steps: processedSteps,
    loading: stepsLoading,
    error: stepsError,
  } = useFormSteps(schema?.steps, { layouts });

  const [formState, setFormState] = useState<Record<string, any>>(() => {
    const query = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });

    try {
      return JSON.parse(query.formData as string);
    } catch (e) {
      return query.formData ?? {};
    }
  });
  const handleFormReset = () => setFormState({});
  const handleChange = useCallback(
    (e: IChangeEvent) => {
      setFormState(e.formData);
    },
    [setFormState],
  );

  useEffect(() => {
    if (templateRef) publish(creationTemplateNavigatedTopic, { templateRef });
  }, [publish, templateRef]);

  const handleCreate = async () => {
    const { taskId } = await scaffolderApi.scaffold({
      templateRef,
      values: formState,
      secrets: secretsContext?.secrets,
    });

    const formParams = qs.stringify(
      { formData: formState },
      { addQueryPrefix: true },
    );
    const newUrl = `${window.location.pathname}${formParams}`;
    // We use direct history manipulation since useSearchParams and
    // useNavigate in react-router-dom cause unnecessary extra rerenders.
    // Also make sure to replace the state rather than pushing to avoid
    // extra back/forward slots.
    window.history?.replaceState(null, document.title, newUrl);

    navigate(scaffolderTaskRoute({ taskId }));
  };

  const loading = schemaLoading || stepsLoading;

  const error = schemaError || stepsError;

  if (error instanceof Error) {
    alertApi.post({
      error: new CustomError('Failed to load template', error.message),
      severity: 'error',
    });
    return <Navigate to={rootRoute()} />;
  }
  if (!loading && !schema) {
    alertApi.post({ message: 'Template not found', severity: 'error' });
    return <Navigate to={rootRoute()} />;
  }

  return (
    <AnalyticsContext attributes={{ entityRef: templateRef }}>
      <WbPage themeId="home">
        <WbHeader
          pageTitleOverride={schema?.title}
          breadcrumbs={
            <WbBreadcrumbs aria-label="breadcrumb">
              <WbBreadcrumb>Builder</WbBreadcrumb>
              <WbBreadcrumb to={breadcrumbsLinkWithQueryString}>
                Templates
              </WbBreadcrumb>
              <WbBreadcrumb>{schema?.title}</WbBreadcrumb>
            </WbBreadcrumbs>
          }
          title={schema?.title}
        />
        <Content>
          {loading && <LinearProgress data-testid="loading-progress" />}
          {!loading && schema && processedSteps && (
            <WbCard title="Template Wizard">
              <MultiStepJsonForm
                formData={{ ...formState, templateRef }}
                fields={customFieldExtensions}
                onChange={handleChange}
                additionalSteps={[
                  reviewStep(
                    'Review and Create',
                    'Create',
                    formState,
                    processedSteps,
                    handleFormReset,
                    handleCreate,
                  ),
                ]}
                steps={processedSteps}
              />
            </WbCard>
          )}
        </Content>
      </WbPage>
    </AnalyticsContext>
  );
};
